/**
 * CardStack - Swipeable stack of generated cards
 *
 * Shows generated cards in a swipeable stack with navigation dots.
 * Supports tap to expand/edit individual cards.
 * If only 1 card, renders inline without stack navigation.
 */

import { useState, useCallback, useRef } from 'react'
import type { GeneratedCard } from '@shared/schemas'
import { PreviewCard } from './PreviewCard'

interface CardStackProps {
  cards: GeneratedCard[]
  imageUrl?: string
  onEdit: (index: number, card: GeneratedCard) => void
  onDiscard?: (index: number) => void
  onDone: () => void
}

export function CardStack({ cards, imageUrl, onEdit, onDiscard, onDone }: CardStackProps) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const touchStartX = useRef(0)
  const touchDeltaX = useRef(0)

  // Clamp currentIndex to valid range (prevents crash after discard)
  const safeIndex = Math.min(currentIndex, cards.length - 1)
  if (safeIndex !== currentIndex && cards.length > 0) {
    setCurrentIndex(safeIndex)
  }

  const goTo = useCallback((index: number) => {
    if (index < 0 || index >= cards.length) return
    setCurrentIndex(index)
  }, [cards.length])

  const goNext = useCallback(() => {
    if (safeIndex < cards.length - 1) goTo(safeIndex + 1)
  }, [safeIndex, cards.length, goTo])

  const goPrev = useCallback(() => {
    if (safeIndex > 0) goTo(safeIndex - 1)
  }, [safeIndex, goTo])

  // Touch handlers for swipe
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX
  }, [])

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    touchDeltaX.current = e.touches[0].clientX - touchStartX.current
  }, [])

  const handleTouchEnd = useCallback(() => {
    const threshold = 50
    if (touchDeltaX.current < -threshold) {
      goNext()
    } else if (touchDeltaX.current > threshold) {
      goPrev()
    }
    touchDeltaX.current = 0
  }, [goNext, goPrev])

  // Single card — no stack needed
  if (cards.length === 1) {
    return (
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <span className="text-sm font-bold text-on-surface-variant">
            1 card generated
          </span>
          <button
            onClick={onDone}
            className="px-4 py-1.5 bg-primary text-on-primary rounded-full font-semibold text-sm"
          >
            Done
          </button>
        </div>
        <PreviewCard
          card={cards[0]}
          imageUrl={imageUrl}
          onEdit={(card) => onEdit(0, card)}
          onDiscard={onDiscard ? () => onDiscard(0) : undefined}
        />
      </div>
    )
  }

  const card = cards[safeIndex]

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <span className="text-sm font-bold text-on-surface-variant">
          {safeIndex + 1} / {cards.length} cards
        </span>
        <button
          onClick={onDone}
          className="px-4 py-1.5 bg-primary text-on-primary rounded-full font-semibold text-sm"
        >
          Done
        </button>
      </div>

      {/* Card container with swipe */}
      <div
        className="relative overflow-hidden rounded-xl"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <PreviewCard
          card={card}
          imageUrl={imageUrl}
          onEdit={(editedCard) => onEdit(safeIndex, editedCard)}
          onDiscard={onDiscard ? () => onDiscard(safeIndex) : undefined}
        />
      </div>

      {/* Navigation dots */}
      <div className="flex items-center justify-center gap-2">
        <button
          onClick={goPrev}
          disabled={safeIndex === 0}
          className="w-8 h-8 flex items-center justify-center rounded-full bg-surface-soft border border-hairline text-on-surface-variant disabled:opacity-30"
          aria-label="Previous card"
        >
          <span className="material-symbols-outlined text-[18px]">chevron_left</span>
        </button>

        <div className="flex gap-1.5">
          {cards.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              className={`w-2.5 h-2.5 rounded-full transition-all ${
                i === safeIndex
                  ? 'bg-primary w-6'
                  : 'bg-muted/30'
              }`}
              aria-label={`Go to card ${i + 1}`}
            />
          ))}
        </div>

        <button
          onClick={goNext}
          disabled={safeIndex === cards.length - 1}
          className="w-8 h-8 flex items-center justify-center rounded-full bg-surface-soft border border-hairline text-on-surface-variant disabled:opacity-30"
          aria-label="Next card"
        >
          <span className="material-symbols-outlined text-[18px]">chevron_right</span>
        </button>
      </div>

      {/* Card type label */}
      <div className="flex justify-center">
        <span className="px-3 py-1 bg-surface-soft rounded-full text-xs font-bold text-on-surface-variant">
          {card.pos}
        </span>
      </div>
    </div>
  )
}
